/* eslint-disable no-magic-numbers */
import {
  allHighlightReactionsSelectorOfCurrentMap,
  currentDrawerReactionSelector,
  highlightReactionIdsForSelectedSearchElementSelector,
} from '@/redux/reactions/reactions.selector';
import { NewReaction } from '@/types/models';
import { LinePoint } from '@/types/reactions';
import { usePointToProjection } from '@/utils/map/usePointToProjection';
import { Feature } from 'ol';
import VectorSource from 'ol/source/Vector';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Geometry } from 'ol/geom';
import { getReactionLineSegments } from '@/components/Map/MapViewer/utils/config/reactionsLayer/getReactionLineSegments';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { getLineFeature } from './getLineFeature';

const getReactionLines = (reaction: NewReaction): LinePoint[] =>
  getReactionLineSegments(reaction).map(segment => [
    {
      x: segment.x1,
      y: segment.y1,
    },
    { x: segment.x2, y: segment.y2 },
  ]);

/**
 * Builds the shared {@link VectorSource} with highlighted-reaction line
 * features derived from the Redux store. Map-independent; wrap it with
 * `createReactionsLayer`.
 */
export const useOlMapReactionsSource = (): VectorSource<Feature<Geometry>> => {
  const pointToProjection = usePointToProjection();
  const reactions = useSelector(allHighlightReactionsSelectorOfCurrentMap);
  const activeReactionsIds = useAppSelector(highlightReactionIdsForSelectedSearchElementSelector);
  const currentDrawerReaction = useAppSelector(currentDrawerReactionSelector);

  const reactionsLinesFeatures = useMemo(() => {
    return reactions
      .map(reaction => {
        let isActive = false;
        if (activeReactionsIds.length) {
          isActive = activeReactionsIds.includes(reaction.id);
        } else if (currentDrawerReaction) {
          isActive = currentDrawerReaction.id === reaction.id;
        }
        const linePoints = getReactionLines(reaction);
        return linePoints.map(linePoint => getLineFeature(linePoint, pointToProjection, isActive));
      })
      .flat();
  }, [reactions, pointToProjection, activeReactionsIds, currentDrawerReaction]);

  const vectorSource = useMemo(() => new VectorSource<Feature<Geometry>>(), []);

  useEffect(() => {
    vectorSource.clear();
    vectorSource.addFeatures(reactionsLinesFeatures);
  }, [reactionsLinesFeatures, vectorSource]);

  return vectorSource;
};
